import { GoogleTagManager } from '@next/third-parties/google';
import NextHead from 'next/head';
import Script from 'next/script';
import { useEffect, useState } from 'react';

import { CONSENT_COOKIE, hasConsent, parseConsentCookie } from 'utils/attribution/consent';
import { UTM_COOKIE, UTM_SESSION_KEY } from 'utils/attribution/utm';
import { CHILIPIPER_FORM_IDS } from 'utils/chilipiper/formIds';
import { isEUVisitor } from 'utils/geo/region';
import { onFirstInteraction } from 'utils/layout/onFirstInteraction';
import { MARKETO_BASE_URL } from 'utils/marketo/config';
import { normalizeScript } from 'utils/normalize/normalizeScript';
import { getBreadcrumbSchema } from 'utils/schema/breadcrumbSchema';
import { getOrganizationSchema } from 'utils/schema/organizationSchema';
import { trailingSlash } from 'utils/strings';
import { forceHttps } from 'utils/strings/slugs';

import type { FC } from 'react';
import type { G2RatingOverride } from 'utils/schema/organizationSchema';

// GTM, LinkedIn and the OpenAI pixel must load immediately (stakeholder
// requirement), so they use `afterInteractive`. They are retargeting/measurement
// pixels that must ping on every pageview, including passive sessions and quick
// bounces. Do NOT move them back to `lazyOnload` for a PSI win: that defers them
// to requestIdleCallback after the load event, which on mobile and short visits
// fires far too late or never, so LinkedIn recorded "no loads on pages for
// retargeting". LinkedIn and OpenAI follow the same GDPR rule as Segment
// (LazySegmentScript): EU visitors only get them after accepting the advertising
// category, everyone else gets them right away as before. ChiliPiper is only
// consumed on form submit, which always happens after an interaction, so its
// concierge bundle (841KB transferred) mounts on the first interaction only:
// a fully passive session can never book a meeting, so a timer fallback
// bought nothing and kept that transfer inside the PSI trace.

// Restores the campaign into the URL for a visitor arriving from the docs, so
// GTM and Dreamdata read it on that pageview. The cookie is written by
// utils/attribution/utm in a compact shape ({"s":..,"m":..}); it used to hold
// the raw landing URL, and this script only knew that older shape, so it
// silently restored nothing from June 2026 on. Both shapes are read now, plus
// the same-tab sessionStorage copy, since this runs before AttributionManager.
const HYDRATE_UTM_FROM_COOKIE = `
(function(){try{
  var UTM_KEYS=['utm_source','utm_medium','utm_campaign','utm_term','utm_content','utm_campaign_name','utm_campaign_id'];
  var params=new URLSearchParams(location.search);
  for(var i=0;i<UTM_KEYS.length;i++){if(params.has(UTM_KEYS[i]))return;}
  var ref=document.referrer||'';
  if(!/^https?:\\/\\/(www\\.)?prismatic\\.io\\/docs(\\/|$)/i.test(ref)&&!/^https?:\\/\\/docs\\.prismatic\\.io/i.test(ref))return;
  function readCookie(name){var m=document.cookie.match(new RegExp('(?:^|; )'+name+'=([^;]*)'));return m?decodeURIComponent(m[1]):null;}
  var COMPACT={utm_source:'s',utm_medium:'m',utm_campaign:'c',utm_term:'t',utm_content:'co',utm_campaign_name:'cn',utm_campaign_id:'ci'};
  var utm={};
  var gtmRaw=readCookie('${UTM_COOKIE}');
  if(gtmRaw){
    if(gtmRaw.charAt(0)==='{'){
      try{var c=JSON.parse(gtmRaw);UTM_KEYS.forEach(function(k){var v=c[COMPACT[k]];if(v)utm[k]=v;});}catch(e){}
    }else{
      try{var u=new URL(gtmRaw);UTM_KEYS.forEach(function(k){var v=u.searchParams.get(k);if(v)utm[k]=v;});}catch(e){}
    }
  }
  if(!Object.keys(utm).length){
    try{var sRaw=sessionStorage.getItem('${UTM_SESSION_KEY}');if(sRaw){var s=JSON.parse(sRaw);UTM_KEYS.forEach(function(k){if(s[k])utm[k]=s[k];});}}catch(e){}
  }
  if(!Object.keys(utm).length){
    var wpRaw=readCookie('wisepops_session');
    if(wpRaw){try{var wp=JSON.parse(wpRaw);if(wp&&wp.utm){['source','medium','campaign','term','content'].forEach(function(k){if(wp.utm[k])utm['utm_'+k]=wp.utm[k];});}}catch(e){}}
  }
  if(!Object.keys(utm).length)return;
  Object.keys(utm).forEach(function(k){params.set(k,utm[k]);});
  var qs=params.toString();
  var newUrl=location.pathname+(qs?'?'+qs:'')+location.hash;
  history.replaceState(history.state,'',newUrl);
}catch(e){}})();
`;

// Dreamdata attribution snippets (verbatim from the marketing-supplied tag).
// The main snippet gates its own load on GTM analytics_storage consent; the
// "-cl" variant is the cookieless companion that loads unconditionally.
const INIT_DREAMDATA =
  '!function(){"use strict";var a="dreamdata",t="dreamdata-analytics",e=!1;function i(a){if(function(){try{var a=window&&window.localStorage&&window.localStorage.getItem("DD_DEBUGGER");return"true"===a||"1"===a}catch(a){return!1}}())try{var t=window.console,e=Array.prototype.slice.call(arguments,1),i=t&&t[a]?t[a]:t&&t.log?t.log:null;i&&i.apply(t,e)}catch(a){return}}var dreamdata=window[a]=window[a]||[];if(!dreamdata.initialize)if(dreamdata.invoked)i("warn","Dreamdata snippet included twice.");else{dreamdata.invoked=!0,dreamdata.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","screen","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware","register"],dreamdata.factory=function(t){return function(){if(window[a].initialized)return window[a][t].apply(window[a],arguments);var e=Array.prototype.slice.call(arguments);if(["track","screen","alias","group","page","identify"].indexOf(t)>-1){var i=document.querySelector("link[rel=\'canonical\']");e.push({__t:"bpc",c:i&&i.getAttribute("href")||void 0,p:location.pathname,u:location.href,s:location.search,t:document.title,r:document.referrer})}return e.unshift(t),dreamdata.push(e),dreamdata}};for(var r=0;r<dreamdata.methods.length;r++){var n=dreamdata.methods[r];dreamdata[n]=dreamdata.factory(n)}dreamdata.load=function(e,i){var r=document.createElement("script");r.id=t,r.type="text/javascript",r.async=!0,r.setAttribute("data-global-dreamdata-analytics-key",a),r.src="https://cdn.dreamdata.cloud/scripts/analytics/next/dreamdata.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(r,n),dreamdata._loadOptions=i},dreamdata._writeKey="37c3980f-31f5-420a-85b6-9c8e8751cf33",dreamdata.SNIPPET_VERSION="dreamdata-2.2.6",dreamdata.init=function(){try{if(e)return void i("warn","Dreamdata already initialized, skipping duplicate initialization");dreamdata.load("37c3980f-31f5-420a-85b6-9c8e8751cf33", {"googleEnhancedConversions":true,"formTracking":{"html":true},"intentSources":{"autoGroup":true}}),dreamdata.page(),e=!0}catch(a){i("error","Failed to initialize Dreamdata:",a&&a.message)}},function(){try{if(e)return;if(i("log","✅ dataLayer",window.dataLayer),i("log","✅ google_tag_data",window.google_tag_data),!window.dataLayer||!window.google_tag_data)return void dreamdata.init();if(window.google_tag_data&&window.google_tag_data.ics&&window.google_tag_data.ics.entries&&window.google_tag_data.ics.entries.analytics_storage&&!0===window.google_tag_data.ics.entries.analytics_storage.update||function(){try{var a=localStorage.getItem("framerCookiesConsentMode");if(!a)return!1;var t=JSON.parse(a);return!!t&&!0===t.analytics}catch(a){return!1}}())return i("log","✅ Consent with GTM analytics_storage in google_tag_data granted, initializing Dreamdata"),void dreamdata.init();var a=window.dataLayer.push;window.dataLayer.push=function(){var r=Array.prototype.slice.call(arguments);i("log","✅ DataLayer Change Detected:",r[0]);var n=r[0];return n&&3===n.length&&"consent"===n[0]&&n[2]&&n[2].analytics_storage&&("denied"===n[2].analytics_storage&&(i("log","❌ Consent with GTM analytics_storage in dataLayer denied, removing Dreamdata script"),function(){e=!1;var a=document.getElementById(t);a&&a.remove()}()),"granted"===n[2].analytics_storage&&(i("log","✅ Consent with GTM analytics_storage in dataLayer granted, initializing Dreamdata"),dreamdata.init())),a.apply(this,r)}}catch(a){i("error","Error in initialization logic, falling back to basic init:",a&&a.message),dreamdata.init()}}()}}();';

const INIT_DREAMDATA_CL =
  '!function(){if(!window.dreamdata||!window.dreamdata.initialized){if(window.dreamdata){var e=document.getElementById("dreamdata-analytics");if(e&&e.type&&"text/javascript"==e.type)return}var a="dreamdata-cl",dreamdata=window[a]=window[a]||[];if(!dreamdata.initialize)if(dreamdata.invoked)window.console&&console.error&&console.error("Dreamdata CL snippet included twice.");else{dreamdata.invoked=!0,dreamdata.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","screen","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware","register"],dreamdata.factory=function(e){return function(){if(window[a].initialized)return window[a][e].apply(window[a],arguments);var t=Array.prototype.slice.call(arguments);if(["track","screen","alias","group","page","identify"].indexOf(e)>-1){var r=document.querySelector("link[rel=\'canonical\']");t.push({__t:"bpc",c:r&&r.getAttribute("href")||void 0,p:location.pathname,u:location.href,s:location.search,t:document.title,r:document.referrer})}return t.unshift(e),dreamdata.push(t),dreamdata}};for(var t=0;t<dreamdata.methods.length;t++){var r=dreamdata.methods[t];dreamdata[r]=dreamdata.factory(r)}dreamdata.load=function(e,t){var r=document.createElement("script");r.id="dreamdata-analytics-cl",r.type="text/javascript",r.async=!0,r.setAttribute("data-global-dreamdata-cl-analytics-key",a),r.src="https://cdn.drda.io/scripts/analytics/next/dreamdata.cl.min.js";var i=document.getElementsByTagName("script")[0];i.parentNode.insertBefore(r,i),dreamdata._loadOptions=t},dreamdata._writeKey="37c3980f-31f5-420a-85b6-9c8e8751cf33",dreamdata.SNIPPET_VERSION="dreamdata-cl-2.0.0",dreamdata.load("37c3980f-31f5-420a-85b6-9c8e8751cf33"),dreamdata.page()}}}();';

// Wisepops popups inject their forms dynamically and submit over AJAX, so
// Dreamdata's automatic HTML form tracking (`formTracking:{html:true}` above)
// never sees them and those conversions go untracked. Bind Wisepops' documented
// `after-form-submit` callback to Dreamdata manual tracking: identify the
// visitor by the submitted email, then record the submission. We seed the
// `wisepops` queue stub first (Wisepops itself loads via GTM and may not be
// ready when this runs), so the `listen` registration is queued and applied
// once Wisepops loads. `dreamdata.identify`/`track` are queue stubs too, so
// they are safe to call before the Dreamdata CDN script finishes loading.
const INIT_WISEPOPS_DREAMDATA = `
(function(){
  window.wisepops = window.wisepops || function(){ (window.wisepops.q = window.wisepops.q || []).push(arguments); };
  window.wisepops('listen', 'after-form-submit', function(event){
    try {
      var elements = event && event.target && event.target.elements;
      var email = elements && elements['email'] && elements['email'].value;
      if (!window.dreamdata) return;
      if (email) window.dreamdata.identify(null, { email: email });
      window.dreamdata.track('Form Submitted', { source: 'wisepops' });
    } catch (e) {}
  });
})();
`;

interface SEO {
  slug?: Maybe<string>;
  url?: Maybe<string>;
  title?: Maybe<string>;
  seoTitle?: Maybe<string>;
  description?: Maybe<string>;
  summary?: Maybe<string>;
  noIndex?: Maybe<boolean>;
  openGraphImage?: CloudinaryImageProps;
  featuredImage?: CloudinaryImageProps;
  customScript?: Maybe<string>;
  g2Override?: G2RatingOverride;
}

interface CloudinaryImageProps {
  url: string;
  file?: {
    url: string;
  };
}

const CONSENT_UPDATED_EVENT = 'tracking-consent-updated';

const adPixelsAllowed = (): boolean => !isEUVisitor() || hasConsent('advertising');

// Google Consent Mode. GTM-KB7SNHC sets the default to denied for EEA visitors
// (its Consent Mode template tag), but nothing ever sent the update when they
// accepted on the Segment banner, so every consent-gated tag (GA4, Google Ads,
// Dreamdata, and Facebook/Bing once they require ad_storage) stayed blocked for
// them even after opting in. This sends the saved choice as a Consent Mode update.
//
// On load it runs as an inline script so the update is in the dataLayer before
// GTM's init snippet queues the gtm.js event; from a useEffect it would land
// after the page-view tags were already evaluated. It only sends what was
// accepted: anything refused is already denied by the EEA default, and outside
// the EEA (no default) refusing keeps today's behavior, the same rule
// adPixelsAllowed uses.
const CONSENT_MODE_FROM_COOKIE = `
(function(){try{
  var m=document.cookie.match(new RegExp('(?:^|; )${CONSENT_COOKIE}=([^;]*)'));if(!m)return;
  var p=JSON.parse(decodeURIComponent(m[1]));
  var c=(p&&p.custom&&typeof p.custom==='object')?p.custom:(p||{});
  var s={};
  if(c.marketingAndAnalytics)s.analytics_storage='granted';
  if(c.advertising){s.ad_storage='granted';s.ad_user_data='granted';s.ad_personalization='granted';}
  if(c.functional){s.functionality_storage='granted';s.personalization_storage='granted';}
  if(!Object.keys(s).length)return;
  window.dataLayer=window.dataLayer||[];
  (function(){window.dataLayer.push(arguments);})('consent','update',s);
}catch(e){}})();
`;

type ConsentValue = 'granted' | 'denied';

// Mid-visit changes (the banner saves while GTM is already running). EU visitors
// get the full state so a refusal takes effect immediately; elsewhere only what
// was accepted, matching the inline script above.
const syncConsentMode = (): void => {
  const prefs = parseConsentCookie();
  if (!prefs) {
    return;
  }
  const eu = isEUVisitor();
  const state: Record<string, ConsentValue> = {};
  const set = (keys: string[], granted: boolean) => {
    if (granted || eu) {
      keys.forEach(key => {
        state[key] = granted ? 'granted' : 'denied';
      });
    }
  };
  set(['analytics_storage'], Boolean(prefs.marketingAndAnalytics));
  set(['ad_storage', 'ad_user_data', 'ad_personalization'], Boolean(prefs.advertising));
  set(['functionality_storage', 'personalization_storage'], Boolean(prefs.functional));
  if (!Object.keys(state).length) {
    return;
  }
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  // Consent Mode only reads the `arguments` object gtag() pushes, not a plain array.
  const gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer?.push(arguments);
  } as unknown as (...args: unknown[]) => void;
  gtag('consent', 'update', state);
};

const Head: FC<SEO> = ({ url: slug, customScript, g2Override, ...seo }) => {
  const [chiliPiperReady, setChiliPiperReady] = useState(false);
  const [adPixelsReady, setAdPixelsReady] = useState(false);

  useEffect(() => onFirstInteraction(() => setChiliPiperReady(true)), []);

  // Region and consent are only known in the browser. Re-checked whenever the
  // consent banner saves, so an EU visitor who accepts mid-visit gets the pixels
  // on the same page view. Once loaded they stay for the rest of the visit.
  useEffect(() => {
    const check = () => {
      if (adPixelsAllowed()) {
        setAdPixelsReady(true);
      }
    };
    const onConsentUpdated = () => {
      syncConsentMode();
      check();
    };
    check();
    window.addEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);

    return () => window.removeEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);
  }, []);

  const seoTitle = seo?.seoTitle || seo?.title || '';
  const title = `${seoTitle ? `${seoTitle} | ` : ''}Prismatic`;
  const description = seo?.description || seo?.summary || '';
  const site = 'https://prismatic.io';
  // Versioned filename on purpose: social platforms cache link previews keyed by
  // the image URL, so swapping the file in place leaves every cached unfurl on the
  // old art. Renaming is what actually busts them. Bump the suffix when the default
  // social image changes again.
  const defaultImage = `${site}/images/prismatic-og-2026.png`;
  // Builder keeps the denormalized top-level `url` pointing at the ORIGINAL asset
  // when an image field's file is later swapped, so the current upload only lives
  // on `file.url`. Every visible <Image> in the app renders `file.url`, so prefer
  // it here too: otherwise the og:/twitter: unfurl serves a stale image that no
  // longer matches the page (e.g. an event showing an old webinar poster).
  // forceHttps upgrades the http:// Cloudinary URLs Builder returns so scrapers
  // never get a mixed-content preview.
  const image =
    forceHttps(
      (seo?.openGraphImage?.file?.url || '')?.trim() ||
        (seo?.openGraphImage?.url || '')?.trim() ||
        (seo?.featuredImage?.file?.url || '')?.trim() ||
        (seo?.featuredImage?.url || '')?.trim() ||
        defaultImage,
    ) || defaultImage;
  const isDefaultImage = image === defaultImage;
  const url = trailingSlash(`${site}${slug || ''}`);
  const noIndex = seo?.noIndex;

  const openGraphTitle = title;
  const openGraphDescription = description;

  const openTwitterTitle = title;
  const openTwitterDescription = description;
  const openTwitterImage = image;

  // The WebSite node is a homepage-only entity (a structured-data scan flagged
  // it as duplicated across inner pages); Organization stays sitewide.
  const isHomepage = !slug || slug.split('?')[0].replace(/\/$/, '') === '';
  const organizationSchema = getOrganizationSchema(g2Override, { includeWebSite: isHomepage });
  const breadcrumbSchema = getBreadcrumbSchema(slug);

  const normalizedCustomScript = customScript ? normalizeScript(customScript) : null;
  const pageHasCustomOrganizationSchema =
    normalizedCustomScript?.type === 'application/ld+json' &&
    /"@type"\s*:\s*"Organization"/.test(normalizedCustomScript.content);

  return (
    <>
      {/* Plain inline script on purpose: it must run at parse time, before GTM. */}
      <script id="consent-mode-from-cookie" dangerouslySetInnerHTML={{ __html: CONSENT_MODE_FROM_COOKIE }} />
      {!pageHasCustomOrganizationSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      )}
      {normalizedCustomScript?.content && (
        <script
          type={normalizedCustomScript.type}
          dangerouslySetInnerHTML={{ __html: normalizedCustomScript.content }}
        />
      )}
      <Script
        id="hydrate-utm-from-cookie"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: HYDRATE_UTM_FROM_COOKIE }}
      />
      <Script id="init-dreamdata" strategy="afterInteractive" dangerouslySetInnerHTML={{ __html: INIT_DREAMDATA }} />
      <Script
        id="init-dreamdata-cl"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: INIT_DREAMDATA_CL }}
      />
      <Script
        id="wisepops-dreamdata-tracking"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: INIT_WISEPOPS_DREAMDATA }}
      />
      {adPixelsReady && (
        <>
          <Script
            id="openai-pixel-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              !function(w, d, s, u) {
                if (w.oaiq) return;
                var q = function() { q.q.push(arguments); };
                q.q = [];
                w.oaiq = q;
                var j = d.createElement(s);
                j.async = 1;
                j.src = u;
                var f = d.getElementsByTagName(s)[0];
                f.parentNode.insertBefore(j, f);
              }(window, document, "script", "https://bzrcdn.openai.com/sdk/oaiq.min.js");

              oaiq("init", {
                pixelId: "2ff2krYzungj9jjWjvwmfZ",
                debug: false
              });
            `,
            }}
          />
          <Script
            id="openai-pixel-measure"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              oaiq("measure", "registration_completed", {
                type: "customer_action",
                amount: 0,
                currency: "USD"
              });
            `,
            }}
          />
          <Script
            id="linkedin-partner-id"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              _linkedin_partner_id = "2547540";
              window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
              window._linkedin_data_partner_ids.push(_linkedin_partner_id);
            `,
            }}
          />
          <Script
            id="linkedin-insight"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              (function(l) {
                if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
                window.lintrk.q=[]}
                var s = document.getElementsByTagName("script")[0];
                var b = document.createElement("script");
                b.type = "text/javascript";b.async = true;
                b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
                s.parentNode.insertBefore(b, s);
              })(window.lintrk);
            `,
            }}
          />
        </>
      )}
      {chiliPiperReady && (
        <Script
          id="chilipiper-concierge"
          src="https://prismatic.chilipiper.com/concierge-js/cjs/concierge.js"
          strategy="lazyOnload"
          crossOrigin="anonymous"
          onLoad={() => {
            const cp = (
              window as unknown as {
                ChiliPiper?: { deploy: (tenant: string, router: string, opts: Record<string, unknown>) => void };
              }
            ).ChiliPiper;
            if (cp?.deploy) {
              cp.deploy('prismatic', 'ae-intro-call-router', {
                formType: 'Marketo',
                formIds: [...CHILIPIPER_FORM_IDS],
              });
              // ChiliPiper injects a hidden cross-domain iframe (class="chilipiper-frame")
              // for postMessage communication. It ships without a `title` attribute,
              // which Lighthouse a11y flags. Tag any chilipiper iframe (deployed now
              // or later by additional `cp.submit` calls) so screen readers and audits
              // get a meaningful frame name.
              const labelChiliPiperFrames = () => {
                document.querySelectorAll('iframe.chilipiper-frame:not([title])').forEach(el => {
                  (el as HTMLIFrameElement).title = 'ChiliPiper scheduler';
                });
              };
              labelChiliPiperFrames();
              window.setTimeout(labelChiliPiperFrames, 500);
              window.setTimeout(labelChiliPiperFrames, 2000);
            }
          }}
        />
      )}
      <GoogleTagManager gtmId="GTM-KB7SNHC" />
      <NextHead>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content={`${noIndex ? 'no' : ''}index, ${noIndex ? 'no' : ''}follow`} />
        <link rel="canonical" href={url} />
        {/* The hero image preload that lived here in 83af75ec was duplicating
            next/image's own auto-preload (triggered by `priority` on
            LandingPageHero's Image). Production HTML ended up with two
            `<link rel="preload" as="image">` tags for the same Cloudinary
            asset with different srcset granularity, the browser arbitrated
            poorly, and PSI mobile LCP regressed from 3.7s to 15.2s. Removing
            ours and letting next/image be the single source of truth. */}

        <meta property="og:site_name" content="Prismatic" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={openGraphTitle} />
        <meta property="og:description" content={openGraphDescription} />
        <meta property="og:image" content={image} />
        {/* Only declared for the default asset, whose dimensions we actually know.
            Builder-supplied images are arbitrary sizes, and stating the wrong ones
            is worse than omitting them - scrapers fall back to reading the file. */}
        {isDefaultImage && (
          <>
            <meta property="og:image:width" content="2400" />
            <meta property="og:image:height" content="1260" />
          </>
        )}
        <meta property="og:image:alt" content={`${seoTitle || 'Prismatic'} - Integration platform for B2B SaaS`} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={url} />
        <meta property="twitter:title" content={openTwitterTitle} />
        <meta property="twitter:description" content={openTwitterDescription} />
        <meta name="twitter:image" content={openTwitterImage} />

        <link rel="icon" type="image/png" href="/favicon-48x48.png" sizes="48x48" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <meta name="apple-mobile-web-app-title" content="Prismatic" />
        <link rel="preconnect" href="https://res.cloudinary.com" crossOrigin="" />
        <link rel="dns-prefetch" href={MARKETO_BASE_URL} />
        <link rel="dns-prefetch" href="https://cdn.builder.io" />
        <link rel="dns-prefetch" href="https://prismatic.chilipiper.com" />
        <link rel="dns-prefetch" href="https://cdn.segment.com" />
        <link rel="dns-prefetch" href="https://cdn.rudderlabs.com" />
        <link rel="dns-prefetch" href="https://prismaticheahx.dataplane.rudderstack.com" />
        <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
        <link rel="dns-prefetch" href="https://snap.licdn.com" />
        <link rel="dns-prefetch" href="https://bzrcdn.openai.com" />
        <link rel="dns-prefetch" href="https://scripts.clarity.ms" />
        <link rel="dns-prefetch" href="https://cdn.dreamdata.cloud" />
        <link rel="dns-prefetch" href="https://cdn.drda.io" />
        <link rel="manifest" href="/site.webmanifest" />
      </NextHead>
    </>
  );
};

export default Head;
